/**
 * Multi-Persona Improvement Results View
 *
 * Shows score table (per persona × per iteration), change summaries,
 * and action buttons after the improvement loop completes.
 *
 * Story: 24.4 - Multi-Persona Content Improvement Engine
 */

import { useState } from 'react';
import type {
  MultiPersonaIterationState,
  EpisodeFields,
  StopReason,
} from '../hooks/useImprovementLoop';

interface ImprovementResultsViewProps {
  baseline: MultiPersonaIterationState;
  iterations: MultiPersonaIterationState[];
  stopReason: StopReason;
  stopMessage: string | null;
  onSaveToEpisode: (episodeData: EpisodeFields) => void;
  onDiscard: () => void;
  onDownloadReport: () => void;
}

function getScoreColor(score: number): string {
  if (score >= 8) return '#16a34a'; // green-600
  if (score >= 6) return '#ca8a04'; // yellow-600
  return '#dc2626'; // red-600
}

export function ImprovementResultsView({
  baseline,
  iterations,
  stopReason,
  stopMessage,
  onSaveToEpisode,
  onDiscard,
  onDownloadReport,
}: ImprovementResultsViewProps) {
  const [showEpisodeData, setShowEpisodeData] = useState(false);

  // Get the final episode data (from last iteration or baseline)
  const finalIteration = iterations.length > 0 ? iterations[iterations.length - 1] : baseline;
  const finalEpisodeData = finalIteration.episodeData;

  // Collect all persona names
  const personaNames = baseline.perPersonaResults.map((r) => r.personaName);

  // Stop reason display
  const stopReasonLabel =
    stopReason === 'threshold_met'
      ? 'All judges scored 8+'
      : stopReason === 'max_iterations'
        ? 'Max iterations reached'
        : stopReason === 'cancelled'
          ? 'Cancelled by user'
          : stopReason === 'error'
            ? 'Error occurred'
            : 'Completed';

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Improvement Results</h3>
        <span
          className={`text-xs px-2 py-1 rounded ${
            stopReason === 'threshold_met'
              ? 'bg-green-100 text-green-700'
              : stopReason === 'error'
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          {stopMessage || stopReasonLabel}
        </span>
      </div>

      {/* Score Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 pr-3 font-medium text-gray-600">Persona</th>
              <th className="text-center py-2 px-2 font-medium text-gray-600">Baseline</th>
              {iterations.map((it) => (
                <th key={it.iteration} className="text-center py-2 px-2 font-medium text-gray-600">
                  Iter {it.iteration}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {personaNames.map((name) => (
              <tr key={name} className="border-b border-gray-100">
                <td className="py-1.5 pr-3 text-gray-700">{name}</td>
                <td className="py-1.5 px-2 text-center">
                  <ScoreCell
                    score={
                      baseline.perPersonaResults.find((r) => r.personaName === name)?.judgeResult
                        .overallScore
                    }
                  />
                </td>
                {iterations.map((it) => (
                  <td key={it.iteration} className="py-1.5 px-2 text-center">
                    <ScoreCell
                      score={
                        it.perPersonaResults.find((r) => r.personaName === name)?.judgeResult
                          .overallScore
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
            {/* Average row */}
            <tr className="border-t-2 border-gray-300 font-semibold">
              <td className="py-2 pr-3 text-gray-900">Average</td>
              <td className="py-2 px-2 text-center">
                <ScoreCell score={baseline.summary?.unifiedScore} bold />
              </td>
              {iterations.map((it) => (
                <td key={it.iteration} className="py-2 px-2 text-center">
                  <ScoreCell score={it.summary?.unifiedScore} bold />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Change Summary */}
      {iterations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Change Summary</h4>
          <div className="space-y-2">
            {iterations.map((it) => (
              <div key={it.iteration} className="bg-gray-50 rounded p-2">
                <p className="text-xs font-medium text-gray-800">Iteration {it.iteration}</p>
                <p className="text-xs text-gray-600 mt-0.5">{it.changesApplied}</p>
                {it.proposal && Object.keys(it.proposal.fieldRationales).length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {Object.entries(it.proposal.fieldRationales).map(([field, rationale]) => (
                      <p key={field} className="text-[10px] text-gray-500">
                        <span className="font-medium">{field}:</span> {rationale}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improved Episode Data (collapsible) */}
      <div>
        <button
          onClick={() => setShowEpisodeData(!showEpisodeData)}
          className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-gray-900"
        >
          <span className="text-xs text-gray-400">{showEpisodeData ? '▼' : '▶'}</span>
          Improved Episode Data
        </button>
        {showEpisodeData && (
          <div className="mt-2 space-y-2 text-xs">
            <div>
              <p className="font-medium text-gray-700">Context</p>
              <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                {finalEpisodeData.context}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Scenario</p>
              <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                {finalEpisodeData.scenario}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Goals</p>
              <ol className="list-decimal list-inside text-gray-600 bg-gray-50 p-2 rounded">
                {finalEpisodeData.goals.map((g, i) => (
                  <li key={i}>{g}</li>
                ))}
              </ol>
            </div>
            <div>
              <p className="font-medium text-gray-700">Personas</p>
              <div className="bg-gray-50 p-2 rounded space-y-1">
                {finalEpisodeData.personas.map((p, i) => (
                  <p key={i} className="text-gray-600">
                    <span className="font-medium">
                      {p.name}
                      {p.role ? ` (${p.role})` : ''}:
                    </span>{' '}
                    {p.description}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
        {iterations.length > 0 && (
          <button
            onClick={() => onSaveToEpisode(finalEpisodeData)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
          >
            Save to Episode
          </button>
        )}

        <button
          onClick={onDownloadReport}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
        >
          Download Report
        </button>

        <button
          onClick={onDiscard}
          className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm font-medium"
        >
          Discard All
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function ScoreCell({ score, bold }: { score?: number; bold?: boolean }) {
  if (score === undefined) return <span className="text-gray-400">-</span>;

  const roundedScore = Math.round(score * 10) / 10;
  return (
    <span
      className={bold ? 'font-bold' : 'font-medium'}
      style={{ color: getScoreColor(roundedScore) }}
    >
      {Number.isInteger(roundedScore) ? roundedScore : roundedScore.toFixed(1)}
    </span>
  );
}

// ============================================================================
// Report Generation
// ============================================================================

export function generateReport(
  baseline: MultiPersonaIterationState,
  iterations: MultiPersonaIterationState[],
  stopReason: StopReason,
  stopMessage: string | null
): string {
  const lines: string[] = [
    '# Episode Improvement Report',
    '',
    `**Date:** ${new Date().toISOString().split('T')[0]}`,
    `**Iterations:** ${iterations.length}`,
    `**Stop Reason:** ${stopMessage || stopReason || 'Completed'}`,
    '',
  ];

  // Baseline scores
  lines.push('## Baseline Scores');
  lines.push('');
  lines.push('| Persona | Score |');
  lines.push('|---------|-------|');
  for (const r of baseline.perPersonaResults) {
    lines.push(`| ${r.personaName} | ${r.judgeResult.overallScore} |`);
  }
  if (baseline.summary) {
    lines.push(`| **Average** | **${baseline.summary.unifiedScore.toFixed(1)}** |`);
  }
  lines.push('');

  // Iterations
  for (const it of iterations) {
    lines.push(`## Iteration ${it.iteration}`);
    lines.push('');
    lines.push(`**Changes:** ${it.changesApplied}`);
    lines.push('');

    if (it.proposal) {
      lines.push('### Field Changes');
      for (const [field, rationale] of Object.entries(it.proposal.fieldRationales)) {
        lines.push(`- **${field}:** ${rationale}`);
      }
      lines.push('');
    }

    lines.push('### Scores');
    lines.push('| Persona | Score |');
    lines.push('|---------|-------|');
    for (const r of it.perPersonaResults) {
      lines.push(`| ${r.personaName} | ${r.judgeResult.overallScore} |`);
    }
    if (it.summary) {
      lines.push(`| **Average** | **${it.summary.unifiedScore.toFixed(1)}** |`);
    }
    lines.push('');
  }

  // Final episode data
  const finalIt = iterations.length > 0 ? iterations[iterations.length - 1] : baseline;
  lines.push('## Final Episode Data');
  lines.push('');
  lines.push(`**Context:** ${finalIt.episodeData.context}`);
  lines.push('');
  lines.push(`**Scenario:** ${finalIt.episodeData.scenario}`);
  lines.push('');
  lines.push('**Goals:**');
  for (const g of finalIt.episodeData.goals) {
    lines.push(`- ${g}`);
  }
  lines.push('');
  lines.push('**Personas:**');
  for (const p of finalIt.episodeData.personas) {
    lines.push(`- **${p.name}:** ${p.description}`);
  }

  return lines.join('\n');
}
